import React, { Fragment, useEffect, useState } from "react";
import { LoadingButton } from "@mui/lab";
import { Button, MenuItem, TextField, Typography } from "@mui/material";
import { FieldArray, Formik } from "formik";
import { FaTrash } from "react-icons/fa";
import { toast } from "react-toastify";
import axios from "axios";
import { Modal } from "react-bootstrap";

import API_URL from "../../../redux2";
import { CiSearch } from "react-icons/ci";
import { IoPersonAdd } from "react-icons/io5";
import { ManualBillingSchema } from "../user.util";

const PAYMENT_METHODS = [
  { value: "cbe", label: "CBE" },
  { value: "awash", label: "Awash Bank" },
  { value: "abyssinia", label: "Abyssinia Bank" },
  { value: "amhara", label: "Amhara Bank" },
  { value: "bunna", label: "Bunna Bank" },
  { value: "wegagen", label: "Wegagen Bank" },
  { value: "dashen", label: "Dashen Bank" },
  { value: "zemen", label: "Zemen Bank" },
  { value: "oromia", label: "Oromia Bank" },
  { value: "coop", label: "Cooperative Bank of Oromia" },
  { value: "hibret", label: "Hibret Bank" },
  { value: "wallet", label: "Wallet" },
  { value: "credit", label: "Credit" },
  { value: "short-term-credit", label: "Short-term Credit" },
];

const BILLING_TYPES = [
  { value: "booking", label: "Booking" },
  // { value: "exchangeFee", label: "Exchange" },
  // { value: "refund", label: "Refund" },
];

const BOOKING_TYPES = [
  { value: "booking", label: "Normal" },
  { value: "halfRefund", label: "Half Refund" },
];

const TYPES = [
  { value: "Galileo", label: "Galileo" },
  { value: "NDCManual", label: "NDC" },
  { value: "FZ", label: "Fly Dubai" },
  { value: "HalfRefund", label: "Half Refund" },
];

const TRIP_TYPES = [
  { value: "oneway", label: "One-way" },
  { value: "roundtrip", label: "Round trip" },
  { value: "multi", label: "Multi-city" },
];

const FORM_FIELDS = [
  {
    label: "Transaction ID",
    name: "transactionId",
    type: "text",
  },
  {
    label: "Base Fare",
    name: "baseFare",
    type: "number",
    readOnly: true,
  },
  {
    label: "Service Charge",
    name: "serviceCharge",
    type: "number",
    readOnly: true,
  },
  {
    label: "Total Amount",
    name: "totalAmount",
    type: "number",
    readOnly: true,
  },
];

const FORM_FIELDS_HALF_REFUND = [
  {
    label: "Total Paid Amount",
    name: "totalPaidAmount",
    type: "number",
  },
  {
    label: "To Be Refunded Amount",
    name: "toBeRefunded",
    type: "number",
    readOnly: true,
  },
];

const Passenger_Type = [
  { value: "ADT", label: "ADT" },
  { value: "CNN", label: "CNN" },
  { value: "INF", label: "INF" },
  { value: "LBR", label: "LBR" },
];

const PASSENGER_FIELDS = [
  {
    label: "First Name",
    name: "firstName",
    type: "text",
  },
  {
    label: "Last Name",
    name: "lastName",
    type: "text",
  },
  {
    label: "Ticket Number",
    name: "ticketNumber",
    type: "text",
  },
  {
    label: "Passenger System Fare",
    name: "price",
    type: "number",
  },
  {
    label: "Passenger SC",
    name: "passengerSC",
    type: "number",
  },
];

const FLIGHT_FIELDS = [
  {
    label: "Origin",
    name: "origin",
    type: "text",
  },
  {
    label: "Destination",
    name: "destination",
    type: "text",
  },
  {
    label: "PNR",
    name: "PNR",
    type: "text",
  },
  {
    label: "Carrier",
    name: "carrier",
    type: "text",
    placeholder: "(ET, EK, etc.)",
  },
  {
    label: "Departure Date",
    name: "departureDate",
    type: "date",
  },
];

const BASE_INITIAL_VALUES = {
  transactionId: "",
  baseFare: 0,
  serviceCharge: 0,
  totalAmount: 0,
  paymentMethod: "",
  typeOfBilling: "booking",
  typeOfBooking: "booking",
  typeOfChannel: "",
  halfRefund: {
    toBeRefunded: 0,
    totalPaidAmount: 0,
  },
  flight: {
    origin: "",
    destination: "",
    carrier: "",
    PNR: "",
    departureDate: "",
  },
  tripType: "oneway",
  passengers: [
    {
      firstName: "",
      lastName: "",
      ticketNumber: "",
      price: 0,
      type: "ADT",
      passengerSC: "",
    },
  ],
};

const FormField = ({
  field,
  values,
  handleChange,
  touched,
  errors,
  margin = true,
  placeholder,
  nestedField,
  setFieldValue,
}) => {
  const fieldName = nestedField ? `${nestedField}.${field.name}` : field.name;
  const fieldValue = nestedField
    ? nestedField === "flight" || nestedField === "halfRefund"
      ? values.flight[field.name] || values.halfRefund[field.name]
      : values[nestedField.split(".")[0]][nestedField.split(".")[1]]?.[
          field.name
        ]
    : values[field.name];

  const calculateTotals = (passengers) => {
    const baseFare = passengers.reduce(
      (sum, passenger) => sum + (parseFloat(passenger.price) || 0),
      0,
    );
    const serviceCharge = passengers.reduce(
      (sum, passenger) => sum + (parseFloat(passenger.passengerSC) || 0),
      0,
    );
    const totalAmount = baseFare + serviceCharge;

    return {
      baseFare: baseFare.toFixed(2),
      serviceCharge: serviceCharge.toFixed(2),
      totalAmount: totalAmount.toFixed(2),
    };
  };

  const handleFieldChange = (e) => {
    handleChange(e);

    if (
      nestedField &&
      nestedField.startsWith("passengers.") &&
      (field.name === "price" || field.name === "passengerSC")
    ) {
      const updatedPassengers = [...values.passengers];
      const passengerIndex = parseInt(nestedField.split(".")[1]);
      updatedPassengers[passengerIndex] = {
        ...updatedPassengers[passengerIndex],
        [field.name]: parseFloat(e.target.value) || 0,
      };

      const totals = calculateTotals(updatedPassengers);

      setFieldValue("baseFare", totals.baseFare);
      setFieldValue("serviceCharge", totals.serviceCharge);
      setFieldValue("totalAmount", totals.totalAmount);
    }

    if (nestedField === "halfRefund" && field.name === "totalPaidAmount") {
      const totalPaidAmount = parseFloat(e.target.value) || 0;
      const totalAmount = parseFloat(values.totalAmount) || 0;
      const toBeRefunded = totalAmount - totalPaidAmount;

      setFieldValue("halfRefund.totalPaidAmount", totalPaidAmount);
      setFieldValue("halfRefund.toBeRefunded", toBeRefunded);
    }
  };

  return (
    <div className={`mb-${margin ? "8" : "2"}`}>
      <TextField
        fullWidth
        id={fieldName}
        type={field.type}
        size="small"
        variant="outlined"
        name={fieldName}
        placeholder={placeholder || field.label}
        label={field.label}
        value={fieldValue || ""}
        onChange={handleFieldChange}
        InputProps={{
          readOnly: field.readOnly || false,
        }}
        error={
          touched?.[nestedField]?.[field.name] && errors?.[nestedField]
            ? errors?.[nestedField]?.[field.name]
              ? errors?.[nestedField]?.[field.name]
              : errors?.[field.name]
            : errors?.[field.name]
        }
        helperText={
          touched?.[nestedField]?.[field.name] && errors?.[nestedField]
            ? errors?.[nestedField]?.[field.name]
              ? errors?.[nestedField]?.[field.name]
              : errors?.[field.name]
            : errors?.[field.name]
        }
        InputLabelProps={{ shrink: true }}
        onWheel={(e) => e.target.blur()}
      />
    </div>
  );
};

const SelectField = ({
  name,
  label,
  value,
  options,
  handleChange,
  touched,
  errors,
}) => (
  <div className="mb-8">
    <TextField
      fullWidth
      select
      id={name}
      name={name}
      size="small"
      value={value}
      label={label}
      onChange={handleChange}
      error={touched[name] && Boolean(errors[name])}
      helperText={touched[name] && errors[name]}
    >
      {options.map((option) => (
        <MenuItem key={option.value} value={option.value}>
          {option.label}
        </MenuItem>
      ))}
    </TextField>
  </div>
);

const AddManualBillingModal = ({
  openManualBilling,
  setOpenManualBilling,
  handleManualBilling,
}) => {
  const handleSubmit = (values, { setSubmitting }) => {
    let hasError = false;

    if (values.typeOfBooking === "halfRefund") {
      if (values.halfRefund.totalPaidAmount <= 0) {
        toast.error("Total Paid Amount must be greater than 0");
        hasError = true;
      }
      if (values.halfRefund.toBeRefunded <= 0) {
        toast.error("To Be Refunded Amount must be greater than 0");
        hasError = true;
      }

      if (values.halfRefund.totalPaidAmount > values.totalAmount) {
        toast.error("Total Paid Amount must be less than Total Amount");
        hasError = true;
      }

      if (
        values.halfRefund.totalPaidAmount + values.halfRefund.toBeRefunded !=
        +values.totalAmount
      ) {
        toast.error(
          "Total Paid Amount and To Be Refunded Amount must be equal to Total Amount",
        );
        hasError = true;
      }
    }

    if (hasError) {
      setSubmitting(false);
      return;
    }

    handleManualBilling(values);
    setSubmitting(false);
  };

  const handleClose = () => setOpenManualBilling(false);

  const [initialValues, setInitialValues] = useState(BASE_INITIAL_VALUES);

  const [ticketNumber, setTicketNumber] = useState("");
  const [ticketNumberLoading, setTicketNumberLoading] = useState(false);
  const [ticketNumberError, setTicketNumberError] = useState(false);
  const handleTicketNumberChange = (e) => {
    setTicketNumber(e.target.value);
    setTicketNumberError(false);
  };
  const [ticketData, setTicketData] = useState(null);

  const calculateTotals = (passengers) => {
    const baseFare = passengers.reduce(
      (sum, passenger) => sum + (parseFloat(passenger.price) || 0),
      0,
    );
    const serviceCharge = passengers.reduce(
      (sum, passenger) => sum + (parseFloat(passenger.passengerSC) || 0),
      0,
    );
    const totalAmount = baseFare + serviceCharge;

    return {
      baseFare: baseFare.toFixed(2),
      serviceCharge: serviceCharge.toFixed(2),
      totalAmount: totalAmount.toFixed(2),
    };
  };

  useEffect(() => {
    if (ticketData) {
      const { ETR } = ticketData;

      const newInitialValues = {
        ...BASE_INITIAL_VALUES,
        flight: {
          ...BASE_INITIAL_VALUES.flight,
          origin:
            ETR?.Ticket?.Coupon?.[0]?._attributes?.Origin ||
            ETR?.Ticket?.Coupon?._attributes?.Origin,
          destination:
            ETR?.Ticket?.Coupon?.[ETR?.Ticket?.Coupon?.length - 1]?._attributes
              ?.Destination || ETR?.Ticket?.Coupon?._attributes?.Destination,
          carrier:
            ETR?.Ticket?.Coupon?.[0]?._attributes?.MarketingCarrier ||
            ETR?.Ticket?.Coupon?._attributes?.MarketingCarrier,
          PNR: ETR?._attributes?.ProviderLocatorCode,
          retrievalPnr: ticketData?._attributes?.UniversalRecordLocatorCode,
          departureDate: (() => {
            const date1 = ETR?.Ticket?.Coupon?.[0]?._attributes?.DepartureTime;
            const date2 = ETR?.Ticket?.Coupon?._attributes?.DepartureTime;

            if (date1) {
              const d = new Date(date1);
              return isNaN(d.getTime()) ? "" : d.toISOString().split("T")[0];
            }

            if (date2) {
              const d = new Date(date2);
              return isNaN(d.getTime()) ? "" : d.toISOString().split("T")[0];
            }

            return "";
          })(),
        },
        passengers: [
          {
            firstName:
              ETR?.BookingTraveler?.BookingTravelerName?._attributes?.First,
            lastName:
              ETR?.BookingTraveler?.BookingTravelerName?._attributes?.Last,
            ticketNumber: ETR?.Ticket?._attributes?.TicketNumber,
            price: ETR?._attributes?.TotalPrice?.slice(3),
            type: ETR?.BookingTraveler?._attributes?.TravelerType || "ADT",
            passengerSC: "",
          },
        ],
      };

      const totals = calculateTotals(newInitialValues.passengers);
      newInitialValues.baseFare = totals.baseFare;
      newInitialValues.serviceCharge = totals.serviceCharge;
      newInitialValues.totalAmount = totals.totalAmount;

      setInitialValues(newInitialValues);
    }
  }, [ticketData]);

  const handleSearchByTicketNumber = async () => {
    setTicketNumberLoading(true);
    if (ticketNumber.length < 10) {
      setTicketNumberError("Ticket number must be at least 10 characters");
    } else {
      const response = await axios.post(API_URL + `flight/retrieve-ticket`, {
        documentNumbers: [ticketNumber],
      });

      if (response.data.AirRetrieveDocumentRsp) {
        toast.success("Ticket retrieved successfully");
        setTicketData(response?.data?.AirRetrieveDocumentRsp);
      } else {
        toast.error("Ticket not found");
      }
      setTicketNumberLoading(false);
    }
  };

  const handleSearchPassengerTicket = async (
    ticketNumber,
    index,
    setFieldValue,
    values,
  ) => {
    if (ticketNumber.length < 10) {
      toast.error("Ticket number must be at least 10 characters");
      return;
    }

    try {
      const response = await axios.post(API_URL + `flight/retrieve-ticket`, {
        documentNumbers: [ticketNumber],
      });

      if (response.data.AirRetrieveDocumentRsp) {
        const { ETR } = response.data.AirRetrieveDocumentRsp;

        setFieldValue(
          `passengers.${index}.firstName`,
          ETR?.BookingTraveler?.BookingTravelerName?._attributes?.First || "",
        );
        setFieldValue(
          `passengers.${index}.lastName`,
          ETR?.BookingTraveler?.BookingTravelerName?._attributes?.Last || "",
        );
        setFieldValue(
          `passengers.${index}.price`,
          ETR?._attributes?.TotalPrice?.slice(3) || 0,
        );
        setFieldValue(
          `passengers.${index}.type`,
          ETR?.BookingTraveler?._attributes?.TravelerType || "ADT",
        );

        const totals = calculateTotals(values.passengers);
        setFieldValue(
          "baseFare",
          parseFloat(totals.baseFare) +
            parseFloat(ETR?._attributes?.TotalPrice?.slice(3)) -
            parseFloat(values.passengers[index].price),
        );

        setFieldValue(
          "totalAmount",
          parseFloat(totals.totalAmount) +
            parseFloat(ETR?._attributes?.TotalPrice?.slice(3)) -
            parseFloat(values.passengers[index].price) -
            parseFloat(values.passengers[index].passengerSC),
        );

        toast.success("Passenger data retrieved successfully");
      } else {
        toast.error("Ticket not found");
      }
    } catch (error) {
      toast.error("Error retrieving ticket data");
    }
  };

  return (
    <div>
      <div
        className="cursor-pointer"
        onClick={() => setOpenManualBilling(true)}
      >
        Add Manual Billing
      </div>

      <Modal
        className="modal fade"
        show={openManualBilling}
        onHide={handleClose}
        size="xl"
      >
        <div className="" role="document">
          <div className="">
            <div className="modal-header">
              <h4 className="modal-title fs-20">Add Manual Billing</h4>
              <button
                type="button"
                className="btn-close"
                onClick={handleClose}
                data-dismiss="modal"
              />
            </div>

            <div className="modal-body">
              <i className="text-black flat-icon-cancel-12 close" />
              <div className="add-contact-box">
                {/* search by ticketNumber */}
                <div className="flex flex-col gap-2 mb-4">
                  <h6 className="text-sm font-bold">
                    Search by Ticket Number(For Galileo tickets only)
                  </h6>
                  <div className="flex flex-row gap-2 items-center">
                    <TextField
                      name="ticketNumber"
                      label="Ticket Number"
                      value={ticketNumber}
                      onChange={handleTicketNumberChange}
                      error={ticketNumberError}
                      helperText={ticketNumberError}
                      size="small"
                      variant="outlined"
                      className="w-1/2"
                      // fullWidth
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleSearchByTicketNumber}
                      className="px-4 py-1"
                      disabled={ticketNumberLoading}
                    >
                      {ticketNumberLoading ? "Searching..." : "Search"}
                    </Button>
                  </div>
                </div>
                <Formik
                  initialValues={initialValues}
                  validationSchema={ManualBillingSchema}
                  enableReinitialize
                  onSubmit={handleSubmit}
                >
                  {({
                    values,
                    handleChange,
                    handleSubmit,
                    isSubmitting,
                    errors,
                    touched,
                    setFieldValue,
                  }) => {
                    return (
                      <form onSubmit={handleSubmit}>
                        <div className="flex flex-col justify-center">
                          <div className="row">
                            <Typography variant="h6" sx={{ mb: 2 }}>
                              Type of Booking
                            </Typography>
                            <SelectField
                              name="typeOfBooking"
                              label="Type of Booking"
                              value={values.typeOfBooking}
                              options={BOOKING_TYPES}
                              handleChange={(e) => {
                                handleChange(e);
                                if (e.target.value === "halfRefund") {
                                  setFieldValue("typeOfChannel", "HalfRefund");
                                } else {
                                  setFieldValue("typeOfChannel", "");
                                }
                              }}
                              touched={touched}
                              errors={errors}
                            />
                            {/*<Typography variant="h6" sx={{ mb: 1 }}>
                              Booking Details
                            </Typography>
                             <SelectField
                              name="type"
                              label="Type"
                              value={values.type}
                              options={TYPES}
                              handleChange={handleChange}
                              touched={touched}
                              errors={errors}
                            /> */}
                            {/* Flight Details */}
                            <Typography variant="h6" sx={{ mb: 2 }}>
                              Flight Details
                            </Typography>
                            <div className="flex flex-row gap-2 items-center">
                              {FLIGHT_FIELDS.map((field) => (
                                <FormField
                                  key={field.name}
                                  field={field}
                                  values={values}
                                  handleChange={handleChange}
                                  placeholder={field.placeholder}
                                  touched={touched}
                                  errors={errors}
                                  margin={false}
                                  nestedField="flight"
                                  setFieldValue={setFieldValue}
                                />
                              ))}
                            </div>
                            <div className="grid grid-cols-3 gap-2 mt-2">
                              <SelectField
                                name="tripType"
                                label="Trip Type"
                                value={values.tripType}
                                options={TRIP_TYPES}
                                handleChange={handleChange}
                                touched={touched}
                                errors={errors}
                              />
                            </div>
                            {/* Passengers */}
                            <Typography variant="h6" sx={{ mb: 2 }}>
                              Passengers
                            </Typography>
                            <FieldArray
                              name="passengers"
                              className="flex flex-col gap-6"
                            >
                              {({ push, remove }) => (
                                <Fragment>
                                  {values.passengers.map((passenger, index) => (
                                    <div
                                      key={index}
                                      className="flex flex-col gap-4 items-center p-2 md:flex-row"
                                    >
                                      {PASSENGER_FIELDS.map((field) => (
                                        <FormField
                                          key={`${field.name}-${index}`}
                                          field={field}
                                          values={values}
                                          handleChange={handleChange}
                                          touched={
                                            touched?.[`passengers`]?.[index]
                                          }
                                          errors={
                                            errors?.[`passengers`]?.[index]
                                          }
                                          nestedField={`passengers.${index}`}
                                          margin={false}
                                          setFieldValue={setFieldValue}
                                        />
                                      ))}
                                      <div className="mb-2">
                                        <TextField
                                          fullWidth
                                          select
                                          id={`passengers.${index}.type`}
                                          name={`passengers.${index}.type`}
                                          size="small"
                                          value={passenger.type || "ADT"}
                                          label="Passenger Type"
                                          onChange={handleChange}
                                        >
                                          {Passenger_Type.map((option) => (
                                            <MenuItem
                                              key={option.value}
                                              value={option.value}
                                            >
                                              {option.label}
                                            </MenuItem>
                                          ))}
                                        </TextField>
                                      </div>
                                      <div className="flex gap-3 items-center">
                                        {/* <Button
                                          variant="outlined"
                                          size="small"
                                          onClick={() =>
                                            handleSearchPassengerTicket(
                                              passenger.ticketNumber,
                                              index,
                                              setFieldValue
                                            )
                                          }
                                          disabled={
                                            !passenger.ticketNumber ||
                                            passenger.ticketNumber.length < 10
                                          }
                                        >
                                          Search
                                        </Button> */}
                                        <CiSearch
                                          size={24}
                                          className="text-blue-500 transition-colors duration-200 cursor-pointer hover:text-blue-900"
                                          disabled={
                                            !passenger.ticketNumber ||
                                            passenger.ticketNumber.length < 10
                                          }
                                          onClick={() =>
                                            handleSearchPassengerTicket(
                                              passenger.ticketNumber,
                                              index,
                                              setFieldValue,
                                              values,
                                            )
                                          }
                                        />
                                        {values.passengers.length > 1 && (
                                          <FaTrash
                                            className="w-4 h-4 text-red-500 transition-colors duration-200 cursor-pointer hover:text-red-900"
                                            onClick={() => {
                                              remove(index);
                                              const totals = calculateTotals(
                                                values.passengers,
                                              );
                                              setFieldValue(
                                                "baseFare",
                                                totals.baseFare -
                                                  parseFloat(
                                                    values.passengers[index]
                                                      .price,
                                                  ),
                                              );
                                              setFieldValue(
                                                "serviceCharge",
                                                totals.serviceCharge -
                                                  parseFloat(
                                                    values.passengers[index]
                                                      .passengerSC,
                                                  ),
                                              );
                                              setFieldValue(
                                                "totalAmount",
                                                totals.totalAmount -
                                                  parseFloat(
                                                    values.passengers[index]
                                                      .price,
                                                  ) -
                                                  parseFloat(
                                                    values.passengers[index]
                                                      .passengerSC,
                                                  ),
                                              );
                                            }}
                                            type="button"
                                          />
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                  <div className="flex justify-center items-center mt-2 mb-4">
                                    {/* <button
                                      className="px-4 py-2 text-black rounded-md border-main border-1"
                                      onClick={() =>
                                        push({
                                          firstName: "",
                                          lastName: "",
                                          ticketNumber: "",
                                          price: 0,
                                          type: "ADT",
                                          passengerSC: 0,
                                        })
                                      }
                                      type="button"
                                    >
                                      Add Passenger
                                    </button> */}
                                    <div className="flex gap-2 items-center px-4 py-2 rounded-md border">
                                      <IoPersonAdd
                                        size={24}
                                        className="text-green-500 transition-colors duration-200 cursor-pointer hover:text-green-900"
                                        onClick={() =>
                                          push({
                                            firstName: "",
                                            lastName: "",
                                            ticketNumber: "",
                                            price: 0,
                                            type: "ADT",
                                            passengerSC: "",
                                          })
                                        }
                                      />
                                    </div>
                                  </div>
                                </Fragment>
                              )}
                            </FieldArray>

                            <Typography variant="h6" sx={{ mb: 2 }}>
                              Billing Details
                            </Typography>
                            <div className="flex flex-row gap-2 items-center mb-3">
                              {values.typeOfBooking === "halfRefund" &&
                                FORM_FIELDS_HALF_REFUND.map((field) => (
                                  <FormField
                                    name={field.name}
                                    label={field.label}
                                    key={field.name}
                                    field={field}
                                    values={values}
                                    handleChange={handleChange}
                                    touched={touched}
                                    errors={errors}
                                    setFieldValue={setFieldValue}
                                    margin={false}
                                    nestedField="halfRefund"
                                  />
                                ))}
                            </div>
                            <div className="flex flex-row gap-2 items-center">
                              {FORM_FIELDS.map((field) => (
                                <FormField
                                  name={field.name}
                                  label={field.label}
                                  key={field.name}
                                  field={field}
                                  values={values}
                                  handleChange={handleChange}
                                  touched={touched}
                                  errors={errors}
                                  setFieldValue={setFieldValue}
                                />
                              ))}
                            </div>

                            {/* Payment Method */}
                            <SelectField
                              name="paymentMethod"
                              label="Payment Method"
                              value={values.paymentMethod}
                              options={PAYMENT_METHODS}
                              handleChange={handleChange}
                              touched={touched}
                              errors={errors}
                            />

                            <SelectField
                              name="typeOfChannel"
                              label="Type"
                              value={values.typeOfChannel}
                              options={TYPES}
                              handleChange={handleChange}
                              touched={touched}
                              errors={errors}
                            />
                          </div>

                          <div className="flex gap-4 justify-center items-center w-full">
                            <Button
                              onClick={handleClose}
                              variant="danger"
                              fullWidth
                            >
                              Cancel
                            </Button>
                            <LoadingButton
                              className="mt-8"
                              variant="contained"
                              type="submit"
                              loading={isSubmitting}
                              disabled={isSubmitting}
                              fullWidth
                            >
                              Add Billing
                            </LoadingButton>
                          </div>
                        </div>
                      </form>
                    );
                  }}
                </Formik>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AddManualBillingModal;
